/* ============================================================================
   DexVisibilitySyncer — report which dex tabs this device hides.

   WHY IT EXISTS
   Dex visibility has always been localStorage-only ("nexus.dexVisibility.v1"),
   so nothing about it ever reached us. That makes a whole class of question
   unanswerable: "this trainer never opens Shadow" and "this trainer hid Shadow
   months ago" look identical from the server and mean opposite things — the
   first is a discovery problem, the second is a deliberate choice to respect.
   Support has the same gap; a trainer asking where a dex went cannot be
   answered without asking them to read their own settings back to us.

   PUSH ONLY — THIS IS THE IMPORTANT PART
   It reports, it never adopts. Nothing here reads the server's copy back down,
   because visibility is a per-device choice: a trainer who hides Shadow on
   their phone has not asked for it to vanish on the web portal too. Turning
   this into a two-way sync would be a behaviour change nobody asked for, and
   would be felt as tabs disappearing on a device they were never hidden on.

   It also never blocks anything. A failed report is dropped: the toggle has
   already taken effect locally, which is the part the trainer cares about.
============================================================================ */
import { useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthProvider";
import {
  ALL_DEX_VARIANTS_ORDERED,
  VISIBILITY_EVENT,
  getVisibleDexes,
} from "@/lib/dexVisibilityStore";

/** Coalesce a run of toggles — flipping six tabs in Settings is one report. */
const REPORT_DEBOUNCE_MS = 1200;

/**
 * The hidden set, derived from the visible one so it always matches the tab row
 * exactly. Ordered by the canonical variant order rather than click order, so
 * the same choice produces the same payload and does not look like a change.
 */
export function hiddenDexList(): string[] {
  const visible = getVisibleDexes();
  return ALL_DEX_VARIANTS_ORDERED.filter((v) => !visible.has(v));
}

export function DexVisibilitySyncer() {
  const { isSignedIn, authedFetch } = useAuth();
  // What the server was last told. Skipping an unchanged report keeps a chatty
  // event (VISIBILITY_EVENT also fires on re-render paths) from becoming a
  // PATCH per keystroke.
  const lastSent = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isSignedIn) {
      // Signed out: forget what was sent, so the next account reports its own
      // set from scratch rather than inheriting this one's "no change".
      lastSent.current = null;
      return;
    }

    const report = () => {
      const hidden = hiddenDexList();
      const key = hidden.join(",");
      if (key === lastSent.current) return;
      lastSent.current = key;
      void authedFetch("/api/account/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ hiddenDexes: hidden }),
      }).catch(() => {
        // Never surfaced. Let the next toggle (or the next sign-in) retry.
        lastSent.current = null;
      });
    };

    const schedule = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(report, REPORT_DEBOUNCE_MS);
    };

    // One report on sign-in, so an account that never touches the toggles still
    // tells us "nothing hidden" — silence would otherwise be indistinguishable
    // from an old build that cannot report at all.
    schedule();
    window.addEventListener(VISIBILITY_EVENT, schedule);
    return () => {
      window.removeEventListener(VISIBILITY_EVENT, schedule);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [isSignedIn, authedFetch]);

  return null;
}
